import styled from "styled-components";
import { motion } from "framer-motion";
import { mobile } from "../../ui/media";

/**
 * Farmer-facing invitation band. Unlike FinderCard this is not a single button —
 * it carries two actions (apply, and sign in for returning farmers), so nesting
 * them inside one button would produce invalid, unusable interactive nesting.
 */
export const Band = styled(motion.section)`
  max-width: 1200px;
  margin: ${({ theme }) => theme.spacing(2)} auto 0;
  padding: ${({ theme }) => `0 ${theme.spacing(2)}`};
`;

export const Inner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.accent.primary};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  box-shadow: ${({ theme }) => theme.colors.shadow.low};

  ${mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => theme.spacing(2)};
  }
`;

export const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: ${({ theme }) => theme.spacing(6)};
  height: ${({ theme }) => theme.spacing(6)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  background: ${({ theme }) => theme.colors.background.surface};
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: 24px;
`;

export const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.75)};
  flex: 1;
  min-width: 0;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h3};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Sub = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Points = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  margin: ${({ theme }) => `${theme.spacing(0.5)} 0 0`};
  padding: 0;
  list-style: none;
`;

export const Point = styled.li`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1.25)}`};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.small};
  white-space: nowrap;
`;

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing(1)};
  flex: 0 0 auto;

  ${mobile} {
    width: 100%;
  }
`;

export const ApplyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  /* 48px minimum touch target per the charter's mobile rule. */
  min-height: ${({ theme }) => theme.spacing(6)};
  padding: ${({ theme }) => `${theme.spacing(1.25)} ${theme.spacing(3)}`};
  border: none;
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.accent.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 700;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    transform ${({ theme }) => theme.motion.fast} ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${({ theme }) => theme.colors.accent.primaryHover};
      transform: translateY(-1px);
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.375)}
      ${({ theme }) => theme.colors.border.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none;
  }
`;

export const SignInLink = styled.button`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing(0.5)};
  color: ${({ theme }) => theme.colors.text.link};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  cursor: pointer;
  text-align: center;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      text-decoration: underline;
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.375)}
      ${({ theme }) => theme.colors.border.focus};
    border-radius: ${({ theme }) => `${theme.radius.sm}px`};
  }
`;
